import { useState } from "react";
import { LogoutIcon, PinIcon, UserIcon } from "@/app/components/system/Icons";
import { LocationModal } from "@/app/components/weather/LocationModal";
import { useWeather } from "@/app/lib/data-context";
import { useAuth } from "@/app/lib/auth-context";

interface UserMenuProps {
  dark?: boolean;
}

// Merges the location pill and the sign-out control into one account button.
// Keeps the glass-pill look (and dark-backdrop variant) of the old LocationPill,
// and opens an animated dropdown holding the location and a sign-out action.
export function UserMenu({ dark = false }: UserMenuProps) {
  const { location, setLocation } = useWeather();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const locationLabel = location?.name ?? "Set location";

  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-full cursor-pointer border backdrop-blur-md transition-[background,transform] duration-150 active:scale-[0.96] ${dark ? "text-paper hover:bg-white/15" : "text-ink hover:bg-white/85"}`}
        style={{
          background: dark ? "oklch(1 0 0 / 0.14)" : "oklch(1 0 0 / 0.65)",
          borderColor: dark ? "oklch(1 0 0 / 0.22)" : "oklch(1 0 0 / 0.55)",
        }}
      >
        <UserIcon size={16} stroke={1.8} />
      </button>

      {/* Click-away catcher — closes the menu when clicking anywhere else. */}
      {open && (
        <div className="fixed inset-0 z-40" aria-hidden="true" onClick={() => setOpen(false)} />
      )}

      {/* Dropdown — always mounted so it animates both in and out. */}
      <div
        role="menu"
        className={`absolute right-0 top-full mt-2 z-50 w-60 origin-top-right rounded-[18px] border border-line bg-paper p-1.5 shadow-[0_12px_36px_oklch(0.3_0.05_150/0.18)] transition duration-150 ease-out ${
          open
            ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
            : "opacity-0 scale-95 -translate-y-1 pointer-events-none"
        }`}
      >
        {user && (
          <p className="px-3 pt-1.5 pb-2 text-[12px] text-ink-2 font-sans truncate" title={user.email}>
            {user.email}
          </p>
        )}

        <button
          type="button"
          role="menuitem"
          onClick={() => { setOpen(false); setLocationModalOpen(true); }}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-[12px] text-left bg-transparent border-0 cursor-pointer hover:bg-cream-2 transition-colors duration-150"
        >
          <PinIcon size={16} stroke={1.8} className="text-ink-3 shrink-0" />
          <span className="flex flex-col min-w-0">
            <span className="text-[10.5px] uppercase tracking-[0.06em] text-ink-3 font-medium">Location</span>
            <span className="text-sm text-ink truncate">{locationLabel}</span>
          </span>
        </button>

        <button
          type="button"
          role="menuitem"
          disabled={signingOut}
          onClick={() => { setSigningOut(true); void logout(); }}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-[12px] text-left bg-transparent border-0 cursor-pointer text-ink hover:bg-cream-2 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <LogoutIcon size={16} stroke={1.8} className="text-ink-3 shrink-0" />
          <span className="text-sm">{signingOut ? "Signing out…" : "Sign out"}</span>
        </button>
      </div>

      {locationModalOpen && (
        <LocationModal
          onClose={() => setLocationModalOpen(false)}
          onPick={(loc) => { setLocation({ location: loc }); setLocationModalOpen(false); }}
          current={location}
        />
      )}
    </div>
  );
}
